import { useContext } from 'react';

import { HStack, Skeleton, Text, TextProps } from '@chakra-ui/react';

import { PermissionEnum } from '../../constants/permissions';
import { usePaymentOptions } from '../../hooks/usePaymentOptions';
import { DealContext } from '../../libs/DealContext';
import { AbilityContext } from '../../libs/contextLib';
import { formatMoney } from '../../libs/utils';

type CreditDecisionCommissionProps = {
  isDenied?: boolean;
  creditDecisionId?: number | null;
  creditDecisionRevision?: string | null;
  skeletonSize?: 'sm' | 'lg';
  fontSize?: TextProps['fontSize'];
  lenderFees?: number | null;
};

const CreditDecisionCommission = ({
  creditDecisionId,
  creditDecisionRevision,
  isDenied,
  skeletonSize = 'lg',
  fontSize = 'sm',
  lenderFees,
}: CreditDecisionCommissionProps) => {
  const { deal } = useContext(DealContext);
  const ability = useContext(AbilityContext);
  const canSeeCommission = ability.has(PermissionEnum.SeeCommission);
  const canSeeProfit = ability.has(PermissionEnum.SeeProfit);
  const shouldShowProfit = canSeeCommission && canSeeProfit;

  const {
    paymentOptions: { noProducts },
    isLoading: isPaymentOptionsLoading,
    isUnavailable,
  } = usePaymentOptions({
    dealId: deal?.id,
    creditDecisionId,
    creditDecisionRevision,
    creditDecisionLenderFees: lenderFees,
  });

  const profitToDisplay = (deal?.financial_info?.doc_fee ?? 0) + noProducts.profit;

  return isPaymentOptionsLoading ? (
    <Skeleton h={6} w={shouldShowProfit ? (skeletonSize === 'lg' ? '30%' : '15%') : '15%'} />
  ) : isUnavailable ? (
    canSeeCommission ? (
      <Text fontSize={fontSize}>Unavailable</Text>
    ) : null
  ) : (
    <HStack spacing={1}>
      {canSeeCommission && (
        <Text
          fontSize={fontSize}
          color="white"
          rounded="md"
          bg="green"
          py={0.5}
          px={2}
          {...(isDenied && { color: 'gray.200' })}
        >
          {formatMoney(noProducts.closerCommission)}
        </Text>
      )}
      {shouldShowProfit && (
        <Text
          fontSize={fontSize}
          color="white"
          rounded="md"
          bg="green"
          py={0.5}
          px={2}
          {...(isDenied && { color: 'gray.200' })}
        >
          {formatMoney(profitToDisplay)}
        </Text>
      )}
    </HStack>
  );
};

export default CreditDecisionCommission;
